import type {
  GetOrgAdultsRequest,
  GetOrgAdultsResponse,
  GetOrgYouthsRequest,
  GetOrgYouthsResponse,
  GetUnitAdultsResponse,
  GetUnitOrdersResponse,
  GetUnitParentsResponse,
  GetUnitYouthsResponse,
} from '@appTypes/esb';
import { tagTypes } from '@modules/rtk/constants';
import { esbApi } from '@modules/rtk/esb-api';
import tagFactory from '@modules/rtk/utils/tagFactory';

export const rosterApi = esbApi.injectEndpoints({
  /**
   * @esbEndpoint GET /organizations/v2/:organziationGuid/orders
   */
  endpoints: builder => ({
    getUnitOrders: builder.query<
      GetUnitOrdersResponse,
      {
        organizationGuid: string;
      }
    >({
      query: arg => ({
        url: `/organizations/v2/${arg.organizationGuid}/orders`,
        method: 'GET',
      }),
      providesTags: (_result, error, arg) => {
        if (error) return [];

        return [tagFactory.UnitRenewalOrders.orgOrders(arg.organizationGuid)];
      },
    }),

    /**
     * @esbEndpoint GET /organizations/v2/units/:organziationGuid/youths
     */
    getUnitRosterYouths: builder.query<
      GetUnitYouthsResponse,
      {
        organizationGuid: string;
      }
    >({
      query: arg => ({
        url: `/organizations/v2/units/${arg.organizationGuid}/youths`,
        method: 'GET',
      }),
      providesTags: (_result, error, arg) => {
        if (error) return [];
        const tags = [
          {
            // useful to reset all rosters for a unnit
            id: arg.organizationGuid,
            type: tagTypes.UnitRoster,
          },
          {
            id: `Youths-${arg.organizationGuid}`,
            type: tagTypes.UnitRoster,
          },
        ];

        if (_result?.users) {
          // useful to refresh roster based on a particular youth update
          tags.push(
            ..._result.users.map(user => ({
              id: `RosterYouth-${user.personGuid}`,
              type: tagTypes.UnitRoster,
            })),
          );
        }
        return tags;
      },
    }),

    /**
     * @esbEndpoint GET /organizations/v2/units/:organziationGuid/adults
     */
    getUnitRosterAdults: builder.query<
      GetUnitAdultsResponse,
      {
        organizationGuid: string;
      }
    >({
      query: arg => ({
        url: `/organizations/v2/units/${arg.organizationGuid}/adults`,
        method: 'GET',
      }),
      providesTags: (_result, error, arg) => {
        if (error) return [];

        const tags = [
          {
            // useful to reset all rosters for a unnit
            id: arg.organizationGuid,
            type: tagTypes.UnitRoster,
          },
          {
            id: `Adults-${arg.organizationGuid}`,
            type: tagTypes.UnitRoster,
          },
        ];

        if (_result?.users) {
          // useful to refresh roster based on a particular adult update
          tags.push(
            ..._result.users.map(user => ({
              id: `RosterAdult-${user.personGuid}`,
              type: tagTypes.UnitRoster,
            })),
          );
        }

        return tags;
      },
    }),

    /**
     * @esbEndpoint POST /organizations/v2/:organziationGuid/orgYouths
     */
    getOrgYouths: builder.query<
      GetOrgYouthsResponse,
      {
        organizationGuid: string;
        body: GetOrgYouthsRequest;
      }
    >({
      query: arg => ({
        url: `/organizations/v2/${arg.organizationGuid}/orgYouths`,
        method: 'POST',
        body: arg.body,
      }),
      providesTags: (_result, error, arg) => {
        if (error) return [];
        const youthGuids =
          _result?.members
            ?.map(member => member.personGuid)
            .map(guid => ({
              type: tagTypes.OrganizationRoster,
              id: `OrgYouths-Youth-${guid}`,
            })) || [];
        return [
          {
            // useful to reset all rosters for an organization
            id: arg.organizationGuid,
            type: tagTypes.OrganizationRoster,
          },
          {
            id: `OrgYouths-${arg.organizationGuid}`,
            type: tagTypes.OrganizationRoster,
          },
          // useful to reset the whole roster if we know a youth has changed
          ...youthGuids,
        ];
      },
    }),

    /**
     * @esbEndpoint POST /organizations/v2/:organziationGuid/orgAdults
     */
    getOrgAdults: builder.query<
      GetOrgAdultsResponse,
      {
        organizationGuid: string;
        body: GetOrgAdultsRequest;
      }
    >({
      query: arg => ({
        url: `/organizations/v2/${arg.organizationGuid}/orgAdults`,
        method: 'POST',
        body: arg.body,
      }),
      providesTags: (_result, error, arg) => {
        if (error) return [];
        return [
          {
            // useful to reset all rosters for an organization
            id: arg.organizationGuid,
            type: tagTypes.OrganizationRoster,
          },
          {
            id: `OrgAdults-${arg.organizationGuid}`,
            type: tagTypes.OrganizationRoster,
          },
        ];
      },
    }),

    /**
     * same as POST /organizations/v2/:organizationGuid/orgAdults
     * but multiple requests
     */
    getMultipleOrgAdults: builder.query<
      GetOrgAdultsResponse[],
      {
        orgGuids: string[];
        body: GetOrgAdultsRequest;
      }
    >({
      queryFn: async (arg, _queryApi, _extraOptions, baseQuery) => {
        const requests = arg.orgGuids.map(orgGuid =>
          baseQuery({
            url: `/organizations/v2/${orgGuid}/orgAdults`,
            method: 'POST',
            body: arg.body,
          }),
        );

        const responses = await Promise.all(requests);
        const datas = responses
          .filter(item => !!item.data)
          .map(item => item.data) as GetOrgAdultsResponse[];
        return { data: datas };
      },
      providesTags: (result, error, arg) => {
        if (!result || error) return [];

        return arg.orgGuids.map(orgGuid => ({
          id: `OrgAdults-${orgGuid}`,
          type: tagTypes.OrganizationRoster,
        }));
      },
    }),

    /**
     * same as POST /organizations/v2/:organizationGuid/orgYouths
     * but multiple requests
     */
    getMultipleOrgYouths: builder.query<
      GetOrgYouthsResponse[],
      {
        orgGuids: string[];
        body: GetOrgYouthsRequest;
      }
    >({
      queryFn: async (arg, _queryApi, _extraOptions, baseQuery) => {
        const requests = arg.orgGuids.map(orgGuid =>
          baseQuery({
            url: `/organizations/v2/${orgGuid}/orgYouths`,
            method: 'POST',
            body: arg.body,
          }),
        );

        const responses = await Promise.all(requests);
        const datas = responses
          .filter(item => !!item.data)
          .map(item => item.data) as GetOrgYouthsResponse[];
        return { data: datas };
      },
      providesTags: (result, error, arg) => {
        if (!result || error) return [];

        return arg.orgGuids.map(orgGuid => ({
          id: `OrgYouths-${orgGuid}`,
          type: tagTypes.OrganizationRoster,
        }));
      },
    }),

    /**
     * @esbEndpoint GET /organizations/v2/units/:organziationGuid/parents
     */
    getUnitRosterParents: builder.query<
      GetUnitParentsResponse,
      {
        organizationGuid: string;
      }
    >({
      query: arg => ({
        url: `/organizations/v2/units/${arg.organizationGuid}/parents`,
        method: 'GET',
      }),
      providesTags: (_result, error, arg) => {
        if (error) return [];
        return [
          {
            // useful to reset all rosters for a unnit
            id: arg.organizationGuid,
            type: tagTypes.UnitRoster,
          },
          {
            id: `Parents-${arg.organizationGuid}`,
            type: tagTypes.UnitRoster,
          },
        ];
      },
    }),
  }),
});

export const {
  useGetUnitOrdersQuery,
  useLazyGetUnitOrdersQuery,
  useGetUnitRosterAdultsQuery,
  useGetUnitRosterYouthsQuery,
  useGetUnitRosterParentsQuery,
  useLazyGetUnitRosterAdultsQuery,
  useLazyGetUnitRosterYouthsQuery,
  useLazyGetUnitRosterParentsQuery,
  useGetOrgYouthsQuery,
  useLazyGetOrgYouthsQuery,
  useGetOrgAdultsQuery,
  useLazyGetOrgAdultsQuery,
  useGetMultipleOrgAdultsQuery,
  useGetMultipleOrgYouthsQuery,
} = rosterApi;
